import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { ProductHistory, SaleRecordExpanded } from "../types/sales";

export const exportSalesPdf = (sales: SaleRecordExpanded[]) => {
  const doc = new jsPDF();
  let cursorY = 14;

  // -----------------------------
  // Calculate Totals
  // -----------------------------
const totalRevenue = sales.reduce(
  (sum, s) => sum + Number(s.amount || 0),
  0
);

const totalCost = sales.reduce(
  (sum, s) => sum + Number(s.cost || 0),
  0
);

const totalProfit = (totalRevenue - totalCost).toFixed(2);

  // -----------------------------
  // Title
  // -----------------------------
  doc.setFontSize(16);
  doc.text("Sales History Report", 14, cursorY);
  cursorY += 10;

  // -----------------------------
  // Sales Sections
  // -----------------------------
  sales.forEach((sale) => {
    doc.setFontSize(12);
    // doc.text(`Sale ID: ${sale.id}`, 14, cursorY);
    // cursorY += 6;

    doc.text(`Store: ${sale.store}`, 14, cursorY);
    cursorY += 6;

    doc.text(
      `Date: ${new Date(sale.createdAt).toDateString()} | Payment: ${sale.paymentMethod}`,
      14,
      cursorY
    );
    cursorY += 6;

    doc.text(
      `Revenue: GHS ${sale.amount.toLocaleString()} | Cost: GHS ${sale.cost.toLocaleString()} | Profit: GHS ${(sale.amount - sale.cost).toLocaleString()}`,
      14,
      cursorY
    );
    cursorY += 8;

    autoTable(doc, {
      startY: cursorY,
      head: [["Product", "Qty", "Total Cost", "Unit Price", "Total Price", "Profit"]],
      body: sale.products.map((p) => [
        p.name ?? "-",
        Number(p.quantity ?? 0),
        `GHS ${Number(p.cost ?? 0).toLocaleString()}`,
        `GHS ${Number(p.sellingPrice ?? 0).toLocaleString()}`,
        `GHS ${Number(p.revenue ?? 0).toLocaleString()}`,
        `GHS ${Number(p.revenue - p.cost).toLocaleString()}`,
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [59, 130, 246], // blue
        textColor: 255,
        fontStyle: "bold",
      },
      styles: { fontSize: 10 },
    });

    cursorY = (doc as any).lastAutoTable.finalY + 12;

    if (cursorY > 260) {
      doc.addPage();
      cursorY = 14;
    }
  });

  // -----------------------------
  // Totals Section
  // -----------------------------
  if (cursorY > 220) {
    doc.addPage();
    cursorY = 14;
  }

  doc.setFontSize(14);
  doc.text("Summary Totals", 14, cursorY);
  cursorY += 10;

  autoTable(doc, {
    startY: cursorY,
    head: [["Metric", "Amount (GHS)"]],
    body: [
      ["Total Revenue", totalRevenue.toFixed(2).toLocaleString()],
      ["Total Cost", totalCost.toFixed(2).toLocaleString()],
      ["Total Profit", totalProfit.toLocaleString()],
    ],
    theme: "grid",
    headStyles: {
      fillColor: [59, 130, 246],
      textColor: 255,
      fontStyle: "bold",
    },
    styles: {
      fontSize: 11,
      halign: "right",
    },
    columnStyles: {
      0: { halign: "left" },
    },
  });

  // -----------------------------
  // Save
  // -----------------------------
  doc.save("sales-history.pdf");
};


export const exportProductsPdf = (products: ProductHistory[]) => {
  const doc = new jsPDF();
  let cursorY = 14;

  doc.setFontSize(16);
  doc.text("Product Sales & Inventory Report", 14, cursorY);
  cursorY += 10;

  products.forEach((product, index) => {
    // Add spacing between products
    if (index !== 0) cursorY += 6;

    // Product header
    doc.setFontSize(12);
    doc.text(`Product: ${product.name}     Store: ${product.store}`, 14, cursorY);
    cursorY += 6;

    doc.setFontSize(10);
    doc.text(
      `Price: GHS ${product.sellingPrice} | Sold: ${product.totalSold} | Revenue: GHS ${product.totalRevenue} | Remaining: ${product.remainingStock}`,
      14,
      cursorY
    );
    cursorY += 6;

    /**
     * SALES TABLE
     */
    doc.text("Sales records", 14, cursorY);
    cursorY += 3;
    autoTable(doc, {
      startY: cursorY,
      head: [["Date", "Qty", "Total Cost", "Unit Price", "Total Price", "Profit"]],
      body: product.sales.map((s) => [
        s.date ?? 'N/A',
        s.quantity,
        `GHS ${s.cost ?? 'N/A'}`,
        `GHS ${s.sellingPrice ?? 'N/A'}`,
        `GHS ${s.revenue ?? 'N/A'}`,
        `GHS ${(s.revenue - s.cost).toFixed(2) ?? 'N/A'}`,
      ]),
      theme: "grid",
      styles: { fontSize: 9 },
      headStyles: { fillColor: [30, 64, 175] },
    });

    cursorY = (doc as any).lastAutoTable.finalY + 6;

    /**
     * INVENTORY TABLE
     */
     doc.text("Inventory records", 14, cursorY);
    cursorY += 3;
    autoTable(doc, {
      startY: cursorY,
      head: [["Batch", "Qty Added", "Expiry Date", "Restocked"]],
      body: product.inventory.map((i) => [
        i.batchId,
        i.quantityAdded,
        i.expiryDate,
        i.restockedAt,
      ]),
      theme: "grid",
      styles: { fontSize: 9 },
      headStyles: { fillColor: [30, 64, 175] },
    });

    cursorY = (doc as any).lastAutoTable.finalY + 10;
  });

  doc.save("product-sales-inventory.pdf");
};


